import { useState, useEffect } from 'react';

/**
 * Hook personnalisé pour vérifier les permissions granulaires
 * @returns {Object} Objet contenant les permissions et les fonctions de vérification
 */
export function useGranularPermissions() {
  // Read user and permissions directly from storage to ensure we always have the latest data
  // after a session validation or admin update.
  const getStoredData = () => {
    try {
      const stored = sessionStorage.getItem('pharmacy_user') || localStorage.getItem('pharmacy_user');
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (error) {
      console.error('Error loading user permissions:', error);
    }
    return null;
  };

  const user = getStoredData();
  const permissions = user?.granular_permissions || {};

  // Helper to get nested value by dot notation (e.g., 'caisse.entrees')
  const getNestedValue = (obj, path) => {
    if (!obj || !path) return undefined;
    return path.split('.').reduce((acc, part) => acc && acc[part], obj);
  };

  /**
   * Vérifie si l'utilisateur a une permission spécifique
   * @param {string} module - Le module (revenues, expenses, closures, etc.)
   * @param {string} action - L'action (create, update, delete, view_history, etc.)
   * @returns {boolean}
   */
  const hasPermission = (module, action) => {
    const role = (user?.role || '').toLowerCase();
    // Super admin has all rights
    if (role === 'super_admin') return true;

    // High-level roles have full access to closures, caisse, revenues, expenses and tresorerie
    if (['admin', 'owner', 'gerant'].includes(role)) {
      if (['closures', 'caisse', 'caisse.entrees', 'caisse.sorties', 'caisse.tresorerie', 'tresorerie', 'revenues', 'expenses'].includes(module)) {
        return true;
      }
    }

    // Aliasing helpers
    const aliasModule = module === 'closures' ? 'caisse.entrees' : (module === 'caisse.entrees' ? 'closures' : (module === 'caisse.sorties' ? 'expenses' : (module === 'expenses' ? 'caisse.sorties' : null)));

    // 1. Check custom permissions first if they exist and action is defined
    const modulePerms = getNestedValue(permissions, module) || permissions?.[module];
    if (modulePerms && modulePerms[action] !== undefined) {
      if (modulePerms[action] === true) return true;
    }

    if (aliasModule) {
      const aliasPerms = getNestedValue(permissions, aliasModule) || permissions?.[aliasModule];
      if (aliasPerms && aliasPerms[action] !== undefined) {
        if (aliasPerms[action] === true) return true;
      }
    }

    // 2. Explicit Fallback: If missing in granular_permissions, fall back to role-based default
    if (!role) return false;
    
    // High-level roles have full access
    if (['admin', 'owner', 'gerant'].includes(role)) return true;
    
    // Comptable has full access except deleting and managing users
    if (role === 'comptable') {
      if (module === 'users') return false;
      if (action === 'delete') return false;
      return true;
    }
    
    // Assistant/Caissier logic
    if (role === 'assistant') {
      if (['suppliers', 'categories'].includes(module) && action === 'view') return true;
      if (['revenues', 'expenses', 'caisse.entrees', 'caisse.sorties'].includes(module)) {
        return ['view_history', 'view_totals', 'view_all_users', 'filter_period'].includes(action);
      }
      if (['closures', 'caisse.entrees'].includes(module)) {
        return ['view_history', 'view_totals', 'filter_period'].includes(action);
      }
      if (['tresorerie', 'caisse.tresorerie', 'pnl'].includes(module)) {
        return ['view', 'view_totals', 'filter_period'].includes(action);
      }
      return false;
    }
    
    if (role === 'caissier') {
      if (['suppliers', 'categories'].includes(module) && action === 'view') return true;
      if (['revenues', 'expenses', 'closures', 'caisse.entrees', 'caisse.sorties'].includes(module)) {
        if (action === 'create') return true;
        return ['view_history', 'view_totals', 'filter_period'].includes(action);
      }
      return false;
    }
    
    return false;
  };

  /**
   * Vérifie si l'utilisateur peut créer dans un module
   */
  const canCreate = (module) => hasPermission(module, 'create');

  /**
   * Vérifie si l'utilisateur peut modifier dans un module
   */
  const canUpdate = (module) => hasPermission(module, 'update');

  /**
   * Vérifie si l'utilisateur peut supprimer dans un module
   */
  const canDelete = (module) => hasPermission(module, 'delete');

  /**
   * Vérifie si l'utilisateur peut voir l'historique
   */
  const canViewHistory = (module) => hasPermission(module, 'view_history');

  /**
   * Vérifie si l'utilisateur peut voir les totaux/KPIs
   */
  const canViewTotals = (module) => hasPermission(module, 'view_totals');

  /**
   * Vérifie si l'utilisateur peut filtrer par période
   */
  const canFilterPeriod = (module) => hasPermission(module, 'filter_period');

  /**
   * Vérifie si l'utilisateur peut voir toutes les entrées (vs seulement les siennes)
   */
  const canViewAllUsers = (module) => hasPermission(module, 'view_all_users');

  /**
   * Vérifie si l'utilisateur peut valider les caisses
   */
  const canValidateCash = () => {
    const role = (user?.role || '').toLowerCase();
    if (['super_admin', 'admin', 'gerant', 'owner'].includes(role)) return true;
    return hasPermission('caisse.entrees', 'validate') || hasPermission('closures', 'validate');
  };

  /**
   * Vérifie si l'utilisateur peut gérer les utilisateurs
   */
  const canManageUsers = () => hasPermission('users', 'manage');

  /**
   * Vérifie si l'utilisateur peut gérer les permissions
   */
  const canManagePermissions = () => hasPermission('users', 'manage_permissions');

  /**
   * Récupère la portée de la période pour un module
   */
  const getPeriodScope = (module) => {
    if (user?.role === 'super_admin') return 'all';
    return permissions?.[module]?.period_scope || 'month';
  };

  /**
   * Vérifie si l'utilisateur a au moins une permission pour un module
   */
  const hasAnyPermission = (module) => {
    const role = (user?.role || '').toLowerCase();
    if (role === 'super_admin') return true;
    if (['admin', 'owner', 'gerant'].includes(role)) return true;

    const aliasModule = module === 'closures' ? 'caisse.entrees' : (module === 'caisse.entrees' ? 'closures' : (module === 'caisse.sorties' ? 'expenses' : (module === 'expenses' ? 'caisse.sorties' : null)));

    // Check in permissions object first
    const modulePerms = getNestedValue(permissions, module) || permissions?.[module];
    if (modulePerms) {
      if (typeof modulePerms === 'object') {
        if (Object.values(modulePerms).some(val => val === true)) return true;
      } else if (modulePerms === true) {
        return true;
      }
    }

    if (aliasModule) {
      const aliasPerms = getNestedValue(permissions, aliasModule) || permissions?.[aliasModule];
      if (aliasPerms) {
        if (typeof aliasPerms === 'object') {
          if (Object.values(aliasPerms).some(val => val === true)) return true;
        } else if (aliasPerms === true) {
          return true;
        }
      }
    }
    
    // Fallbacks
    if (role === 'caissier' && ['revenues', 'expenses', 'closures', 'caisse.entrees', 'caisse.sorties'].includes(module)) return true;
    if (role === 'assistant' && ['revenues', 'expenses', 'closures', 'caisse.entrees', 'caisse.sorties', 'tresorerie', 'caisse.tresorerie', 'pnl'].includes(module)) return true;
    if (role === 'comptable') return true;
    
    return false;
  };

  return {
    permissions,
    user,
    hasPermission,
    hasAnyPermission,
    canCreate,
    canUpdate,
    canDelete,
    canViewHistory,
    canViewTotals,
    canFilterPeriod,
    canViewAllUsers,
    canValidateCash,
    canManageUsers,
    canManagePermissions,
    getPeriodScope
  };
}

export default useGranularPermissions;
